/**
 * Contest Simulator — real DK payout Monte Carlo against the user's portfolio.
 *
 * Shows per-lineup expected ROI, cash rate, top-1% rate, and portfolio-level
 * expected profit vs total entry fees. This is what turns "Milly Hit %" into
 * an actual dollar-EV number.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartBar, Play } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PRESET_ORDER = [
  "milly_maker", "20_max", "three_max", "single_entry_150",
  "small_gpp", "flea_flicker", "cash_50_50",
];

function fmtDollars(v, opts = {}) {
  if (v == null || Number.isNaN(v)) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : (opts.showPlus && v > 0 ? "+" : "");
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(2)}`;
}

function fmtPct(v, decimals = 1) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(decimals)}%`;
}

function Stat({ label, value, tone = "neutral", testid, tooltip }) {
  const toneCls = {
    good: "text-[#39FF14]",
    warn: "text-[#FFCC00]",
    bad: "text-[#FF3B30]",
    neutral: "text-white",
  }[tone] || "text-white";
  return (
    <div data-testid={testid} title={tooltip}>
      <div className="text-[9px] mono tracking-widest text-zinc-500 uppercase">{label}</div>
      <div className={`text-lg font-bold mono ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function ContestSimulator() {
  const [presets, setPresets] = useState({});
  const [preset, setPreset] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("wp_active_preset")) || "milly_maker"
  );
  const [entryFee, setEntryFee] = useState("");
  const [nIter, setNIter] = useState(5000);
  const [sigma, setSigma] = useState(0.35);
  const [source, setSource] = useState("smart");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Sprint 60.117 — inline error banner so a failed Run (e.g. 400 "no
  // lineups persisted for source=smart") isn't swallowed by a transient
  // toast that users routinely miss. Persists until the next Run or a
  // successful result lands.
  const [runError, setRunError] = useState(null);
  // Sprint 92 — the auto-sense build is entered across several tournaments, so
  // it is simulated per contest (own field size, entry fee, max-entries and DK
  // payout table) and rolled up. Kept separate from `result` because the shape
  // is a list of contests, not one portfolio.
  const [autoResult, setAutoResult] = useState(null);
  // Sprint 60.140 — Synthetic progress feedback while the sync Monte
  // Carlo runs. The backend endpoint is blocking (no /simulate/progress
  // yet), but users need SOME visual acknowledgement past the button
  // label. We rotate phase labels + tick an elapsed clock so the UI
  // never looks frozen. Real progress polling can layer on later if
  // the sim gets long enough to justify the backend change.
  const [simElapsed, setSimElapsed] = useState(0);
  const [simPhase, setSimPhase] = useState("");

  useEffect(() => {
    if (!busy) { setSimElapsed(0); setSimPhase(""); return; }
    const t0 = Date.now();
    const phases = [
      "Loading portfolio & DK payout curve…",
      "Sampling player scores · Monte Carlo…",
      "Running lineup-vs-field iterations…",
      "Aggregating cash / top-1% / ROI stats…",
      "Finalising expected payout tables…",
    ];
    setSimPhase(phases[0]);
    const clock = setInterval(() => {
      const s = Math.round((Date.now() - t0) / 1000);
      setSimElapsed(s);
      // Cycle labels every ~3s so the phase text feels alive.
      const idx = Math.min(phases.length - 1, Math.floor(s / 3));
      setSimPhase(phases[idx]);
    }, 500);
    return () => clearInterval(clock);
  }, [busy]);

  useEffect(() => {
    api.contestPresets().then((d) => setPresets(d.presets || {})).catch(() => {});
    api.contestSimulateLast(preset).then((d) => {
      if (d?.portfolio) setResult(d);
    }).catch(() => {});
    const onPresetChange = (e) => {
      const next = e?.detail?.preset;
      if (next) {
        setPreset(next);
        api.contestSimulateLast(next).then((d) => {
          if (d?.portfolio) setResult(d);
          else setResult(null);
        }).catch(() => setResult(null));
      }
    };
    window.addEventListener("wp:preset-change", onPresetChange);
    return () => window.removeEventListener("wp:preset-change", onPresetChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        preset,
        n_iterations: nIter,
        sigma,
        source,
      };
      if (entryFee !== "" && !Number.isNaN(Number(entryFee))) {
        payload.entry_fee_override = Number(entryFee);
      }
      if (source === "autosense") {
        const a = await api.contestSimulateAutosense({ n_iterations: nIter, sigma });
        setAutoResult(a);
        setResult(null);
        setRunError(null);
        const roll = a.rollup || {};
        toast.success(
          `Auto-sense simulated — ${roll.contests} tournaments · ${roll.n_lineups} entries`,
          {
            id: "contest-sim-toast",
            description: `Expected profit ${fmtDollars(roll.expected_total_profit, { showPlus: true })} (${(roll.expected_roi_pct ?? 0) > 0 ? "+" : ""}${(roll.expected_roi_pct ?? 0).toFixed(1)}% ROI on ${fmtDollars(roll.total_entry_fees)} of entries)`,
          }
        );
        return;
      }
      const r = await api.contestSimulate(payload);
      setResult(r);
      setAutoResult(null);
      setRunError(null);
      const profit = r.portfolio?.expected_total_payout - r.portfolio?.total_entry_fees;
      const roi = r.portfolio?.expected_roi_pct ?? 0;
      toast.success(
        `Simulation complete — ${r.portfolio?.n_lineups} lineups`,
        {
          id: "contest-sim-toast",
          description: `Expected profit ${fmtDollars(profit, { showPlus: true })} (${roi > 0 ? "+" : ""}${roi.toFixed(1)}% ROI)`,
        }
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Simulation failed";
      setRunError({
        message: detail,
        source,
        preset,
        hint: source === "autosense"
          ? "Run Auto-sense build on the DK entries panel first — the simulator reads that build, the tournaments it was entered into, and each contest's field size."
          : source === "smart"
          ? "Try generating lineups on the Smart Optimizer tab first, or switch Source above to 'Manual Optimizer' / 'Compare Mode'."
          : source === "raw"
            ? "Try building lineups on the Manual Optimizer tab first, or switch Source above to 'Smart Optimizer'."
            : "Compare Mode needs BOTH a Smart and a Raw lineup batch — generate both before running.",
      });
      toast.error(detail, { id: "contest-sim-toast" });
    } finally {
      setBusy(false);
    }
  };

  const port = result?.portfolio;
  const profit = port?.expected_total_profit ?? 0;
  const isProfit = profit > 0;

  return (
    <section
      data-testid="contest-simulator"
      className="bg-[#121212] border border-zinc-800"
    >
      <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ChartBar size={20} weight="fill" className="text-[#39FF14]" />
          <div>
            <div className="overline flex items-center gap-2 flex-wrap">
              Contest Simulator
              {result?.dk_snapshot?.used && (
                <span
                  data-testid="dk-snapshot-badge"
                  title={`Live DK contest: ${result.dk_snapshot.dk_contest_name || "—"} · ${(result.dk_snapshot.current_entrants || 0).toLocaleString()} entrants${result.dk_snapshot.top_prize ? ` · $${result.dk_snapshot.top_prize.toLocaleString()} top` : ""}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#39FF14]/10 border border-[#39FF14]/40 text-[#39FF14] mono text-[9px] tracking-widest normal-case"
                >
                  <span className="w-1 h-1 bg-[#39FF14] rounded-full animate-pulse" />
                  LIVE DK
                </span>
              )}
            </div>
            <div className="text-sm text-zinc-400">
              Real DK payout curves · expected $ ROI per lineup
            </div>
          </div>
        </div>
        <button
          data-testid="contest-sim-run"
          onClick={run}
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2.5 border-2 uppercase tracking-widest text-xs font-bold transition-colors bg-[#39FF14] text-black border-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40"
        >
          <Play size={14} weight="fill" />
          {busy ? "SIMULATING..." : "RUN SIMULATION"}
        </button>
      </header>

      {/* Sprint 60.140 — Live-simulation progress card. Users clicked
          Run and saw only the button label change to "SIMULATING…",
          with no other feedback until the ~5-30s Monte Carlo finished
          (feels like the app is frozen on slower slates). Synthetic
          progress + rotating phase labels + elapsed clock keeps the
          UI alive; matches the Optimizer's progress pattern. */}
      {busy && (
        <div
          data-testid="contest-sim-progress"
          className="mx-5 my-4 border border-[#39FF14]/40 bg-black/60 p-4"
        >
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Play size={12} weight="fill" className="text-[#39FF14] animate-pulse flex-shrink-0" />
              <span
                data-testid="contest-sim-progress-label"
                className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold truncate"
              >
                {simPhase || "Simulating…"}
              </span>
            </div>
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-400 flex-shrink-0">
              {simElapsed}s elapsed · {nIter.toLocaleString()} iterations · σ {sigma}
            </span>
          </div>
          <div className="relative h-1.5 bg-zinc-900 overflow-hidden">
            {/* Indeterminate barber-pole animation — no backend %,
                but a sliding highlight communicates "still working". */}
            <div
              className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[#39FF14] to-transparent"
              style={{
                animation: "wp-sim-slide 1.6s ease-in-out infinite",
                boxShadow: "0 0 12px rgba(57,255,20,0.4)",
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[9px] mono uppercase tracking-widest text-zinc-500 flex-wrap gap-2">
            <span>Source · {source}</span>
            <span className="text-zinc-600">
              {simElapsed < 3
                ? "Warming up rule engine"
                : simElapsed < 12
                  ? "Running iterations…"
                  : "Almost there — deep sims take longer"}
            </span>
          </div>
          <style>{`
            @keyframes wp-sim-slide {
              0%   { left: -33%; }
              100% { left: 100%; }
            }
          `}</style>
        </div>
      )}

      {/* Sprint 60.117 — inline error banner. Persists past the transient
          toast so users can actually read the reason a Run failed (e.g.
          "no lineups persisted for source=smart") and know exactly what
          to do next. Dismissible; auto-clears on a successful Run. */}
      {runError && (
        <div
          data-testid="contest-sim-error"
          className="mx-5 my-4 border-2 border-[#FF3B30]/60 bg-[#FF3B30]/10 p-4 flex items-start gap-3"
        >
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF3B30]/20 border border-[#FF3B30]/60 inline-flex items-center justify-center">
            <span className="text-[#FF3B30] font-black text-lg">!</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="mono text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">
              Simulation failed · source {runError.source}
            </div>
            <div className="text-sm text-white mt-0.5">{runError.message}</div>
            {runError.hint && (
              <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{runError.hint}</div>
            )}
          </div>
          <button
            type="button"
            data-testid="contest-sim-error-dismiss"
            onClick={() => setRunError(null)}
            className="flex-shrink-0 mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white px-2 py-1 border border-zinc-800 hover:border-zinc-500 transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="p-5 border-b border-zinc-800 grid grid-cols-2 md:grid-cols-5 gap-3">
        <label className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1">
          Contest
          <select
            data-testid="contest-sim-preset"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          >
            {PRESET_ORDER.filter((k) => presets[k]).map((k) => (
              <option key={k} value={k}>{presets[k].label}</option>
            ))}
          </select>
        </label>
        <label className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1">
          Entry Fee ($)
          <input
            data-testid="contest-sim-entry-fee"
            type="number"
            step="0.01"
            placeholder="default"
            value={entryFee}
            onChange={(e) => setEntryFee(e.target.value)}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          />
        </label>
        <label className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1">
          Iterations
          <select
            value={nIter}
            onChange={(e) => setNIter(Number(e.target.value))}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          >
            <option value={2500}>2,500 (fast)</option>
            <option value={5000}>5,000 (default)</option>
            <option value={10000}>10,000 (accurate)</option>
            <option value={20000}>20,000 (deep)</option>
          </select>
        </label>
        <label
          className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1"
          title={
            "Volatility · how boom-bust to assume the slate plays out.\n\n" +
            "Under the hood this is the σ (sigma) of each player's Monte Carlo " +
            "score around their projection, expressed as a fraction of the projection " +
            "itself. Higher volatility widens the score distribution — more chalk-busts, " +
            "more contrarian ceilings, more variance in your simulated cash / top-1% rate.\n\n" +
            "• Chalky (σ 0.25) — favorites-heavy Vegas week, low pace, few injuries\n" +
            "• Standard (σ 0.35) — default NFL volatility\n" +
            "• Boom/Bust (σ 0.45) — thin depth, weather, wide dispersion\n" +
            "• Extreme (σ 0.55) — Week 18 / massive injury news / stack lotto"
          }
        >
          Volatility
          <select
            data-testid="contest-sim-sigma"
            value={sigma}
            onChange={(e) => setSigma(Number(e.target.value))}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          >
            <option value={0.25}>Chalky · σ 0.25</option>
            <option value={0.35}>Standard · σ 0.35 (default)</option>
            <option value={0.45}>Boom / Bust · σ 0.45</option>
            <option value={0.55}>Extreme · σ 0.55</option>
          </select>
        </label>
        <label className="text-[10px] mono tracking-widest text-zinc-500 flex flex-col gap-1">
          Lineup Source
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="bg-black border border-zinc-800 text-white text-xs mono px-2 py-1.5"
          >
            <option value="smart">Last Smart Optimizer Run</option>
            <option value="raw">Last Manual Optimizer Run</option>
            <option value="compare">Compare · Smart vs Manual (A/B)</option>
            <option value="autosense">Auto-Sense Build · every tournament</option>
            <option value="split">Portfolio Split Batch</option>
          </select>
        </label>
      </div>

      {autoResult && (
        <div data-testid="contest-sim-autosense" className="border-b border-zinc-800">
          <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4 bg-black/30">
            <Stat label="Tournaments" value={autoResult.rollup?.contests ?? 0}
                  testid="autosense-contests" tooltip="Each simulated separately with its own field size, entry fee, max-entries rule and DK payout table" />
            <Stat label="Entries" value={autoResult.rollup?.n_lineups ?? 0} testid="autosense-entries" />
            <Stat label="Entry Fees" value={fmtDollars(autoResult.rollup?.total_entry_fees)} testid="autosense-fees" />
            <Stat label="Expected Profit"
                  value={fmtDollars(autoResult.rollup?.expected_total_profit, { showPlus: true })}
                  tone={(autoResult.rollup?.expected_total_profit ?? 0) > 0 ? "good" : "bad"}
                  testid="autosense-profit" />
            <Stat label="Portfolio ROI"
                  value={`${(autoResult.rollup?.expected_roi_pct ?? 0) > 0 ? "+" : ""}${(autoResult.rollup?.expected_roi_pct ?? 0).toFixed(1)}%`}
                  tone={(autoResult.rollup?.expected_roi_pct ?? 0) > 0 ? "good" : "bad"}
                  testid="autosense-roi" />
          </div>
          <div className="overflow-x-auto">
            <table data-testid="contest-sim-autosense-table" className="w-full min-w-[720px] text-xs mono">
              <thead className="bg-black/50 text-zinc-500 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="px-3 py-2 text-left">Tournament</th>
                  <th className="px-3 py-2">Entries</th>
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Fee</th>
                  <th className="px-3 py-2">Cash line</th>
                  <th className="px-3 py-2">Cash %</th>
                  <th className="px-3 py-2">Top 1%</th>
                  <th className="px-3 py-2">Profit</th>
                  <th className="px-3 py-2">ROI</th>
                  <th className="px-3 py-2">Payouts</th>
                </tr>
              </thead>
              <tbody>
                {(autoResult.contests || []).map((c) => {
                  const p = c.portfolio || {};
                  const roi = p.expected_roi_pct ?? 0;
                  return (
                    <tr key={c.contest_id} className="border-t border-zinc-900"
                        data-testid={`autosense-row-${c.contest_id}`}>
                      <td className="px-3 py-2 text-left text-zinc-200 max-w-[260px] truncate"
                          title={c.contest_name || ""}>{c.contest_name || c.contest_id}</td>
                      <td className="px-3 py-2 text-center">{p.n_lineups ?? c.generated ?? 0}</td>
                      <td className="px-3 py-2 text-center">{(c.field_size_used || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-center">{fmtDollars(c.entry_fee_used)}</td>
                      <td className="px-3 py-2 text-center text-zinc-400">{(p.cash_line_pct ?? 0).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-center">{(p.avg_lineup_cash_rate ?? 0).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-center">{(p.avg_lineup_top_1pct_rate ?? 0).toFixed(2)}%</td>
                      <td className={`px-3 py-2 text-center font-bold ${(p.expected_total_profit ?? 0) > 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                        {fmtDollars(p.expected_total_profit, { showPlus: true })}
                      </td>
                      <td className={`px-3 py-2 text-center font-bold ${roi > 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                        {roi > 0 ? "+" : ""}{roi.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-center text-[10px] text-zinc-500"
                          title={c.error || ""}>
                        {c.error ? "error" : (c.payout_source === "dk" ? "DK live table" : "modelled")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 text-[11px] text-zinc-500">
            {String(autoResult.slate_type || "").toUpperCase() === "SHOWDOWN"
              ? "Captain mode · each lineup scored 1.5× on the crown against a Captain-mode field sampled from this game's ownership."
              : "Classic · field sampled by ownership from the active slate."}{" "}
            {autoResult.iterations?.toLocaleString()} iterations · σ {autoResult.sigma}
          </div>
        </div>
      )}

      {/* Portfolio metrics */}
      {port ? (
        <div data-testid="contest-sim-portfolio" className="p-5 border-b border-zinc-800 grid grid-cols-2 md:grid-cols-6 gap-4"
             style={{
               borderLeftWidth: 4,
               borderLeftColor: isProfit ? "#39FF14" : "#FF3B30",
               borderLeftStyle: "solid",
             }}>
          <Stat
            label="Total Entry Fees"
            value={fmtDollars(port.total_entry_fees)}
            testid="sim-total-fees"
          />
          <Stat
            label="Expected Payout"
            value={fmtDollars(port.expected_total_payout)}
            testid="sim-expected-payout"
          />
          <Stat
            label="Expected Profit"
            value={fmtDollars(port.expected_total_profit, { showPlus: true })}
            tone={isProfit ? "good" : "bad"}
            testid="sim-expected-profit"
          />
          <Stat
            label="Portfolio ROI"
            value={fmtPct(port.expected_roi_pct, 1)}
            tone={port.expected_roi_pct > 0 ? "good" : port.expected_roi_pct > -20 ? "warn" : "bad"}
            testid="sim-roi-pct"
          />
          <Stat
            label="Avg Cash Rate"
            value={fmtPct(port.avg_lineup_cash_rate, 1)}
            testid="sim-cash-rate"
            tooltip={`Cash line = top ${port.cash_line_pct}% of field`}
          />
          <Stat
            label="Top 1% Rate"
            value={fmtPct(port.avg_lineup_top_1pct_rate, 2)}
            tone="warn"
            testid="sim-top1-rate"
          />
        </div>
      ) : null}

      {/* Sprint 60.118 — Compare block: Smart vs Raw A/B result */}
      {result?.compare && (
        <div data-testid="contest-sim-compare" className="p-5 border-b border-zinc-800 bg-black/30">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-[10px] text-zinc-400">Smart vs Manual · Same-Seed A/B</div>
            <div className={`text-[10px] mono px-2 py-1 border font-bold ${
              result.compare.winner === "smart"
                ? "border-[#39FF14] text-[#39FF14]"
                : "border-[#007AFF] text-[#007AFF]"
            }`} data-testid="compare-winner-badge">
              {result.compare.winner.toUpperCase()} WINS · ΔROI {result.compare.roi_delta_pct >= 0 ? "+" : ""}{result.compare.roi_delta_pct}%
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["smart", "raw"].map(k => {
              const b = result.compare[k];
              const isWinner = result.compare.winner === k;
              return (
                <div
                  key={k}
                  data-testid={`compare-${k}-panel`}
                  className={`p-3 border ${isWinner ? "border-[#39FF14]/60 bg-[#39FF14]/5" : "border-zinc-800"}`}
                >
                  <div className={`text-[10px] mono tracking-widest font-bold mb-2 ${isWinner ? "text-[#39FF14]" : "text-zinc-400"}`}>
                    {k === "smart" ? "SMART OPTIMIZER" : "RAW OPTIMIZER"} · {b.n_lineups} LU
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px] mono">
                    <div><span className="text-zinc-500">ROI </span><span className={`font-bold ${b.roi_pct >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>{b.roi_pct.toFixed(1)}%</span></div>
                    <div><span className="text-zinc-500">Cash </span><span className="text-white font-bold">{b.cash_rate_pct.toFixed(1)}%</span></div>
                    <div><span className="text-zinc-500">Top 1% </span><span className="text-[#FFCC00] font-bold">{(b.top1_rate_pct || 0).toFixed(2)}%</span></div>
                    <div><span className="text-zinc-500">EV </span><span className={`font-bold ${b.expected_profit >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>${b.expected_profit.toFixed(0)}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!port && (
        <div data-testid="contest-sim-empty" className="p-8 relative overflow-hidden">
          {/* Distribution-curve placeholder SVG — visualizes what a filled state looks like */}
          <svg viewBox="0 0 400 140" className="w-full max-w-md mx-auto opacity-40" preserveAspectRatio="none">
            <defs>
              <linearGradient id="simCurveGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#39FF14" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#39FF14" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M 0 140 L 20 138 L 50 130 L 90 110 L 130 80 L 170 50 L 200 42 L 230 50 L 270 80 L 310 110 L 350 130 L 380 138 L 400 140 Z"
              fill="url(#simCurveGrad)"
            />
            <path
              d="M 0 140 L 20 138 L 50 130 L 90 110 L 130 80 L 170 50 L 200 42 L 230 50 L 270 80 L 310 110 L 350 130 L 380 138 L 400 140"
              fill="none"
              stroke="#39FF14"
              strokeWidth="1.5"
              strokeOpacity="0.6"
            />
            {/* Cash line marker */}
            <line x1="140" y1="0" x2="140" y2="140" stroke="#39FF14" strokeWidth="0.5" strokeDasharray="2 3" strokeOpacity="0.4" />
            <text x="145" y="14" fill="#39FF14" fontSize="8" opacity="0.5" fontFamily="ui-monospace, monospace">CASH LINE</text>
          </svg>
          <div className="text-center mt-4">
            <div className="heading text-lg font-bold text-white mb-1">No simulation run yet</div>
            <div className="text-xs text-zinc-300 max-w-md mx-auto">
              Generate a lineup portfolio in the Optimizer, then click <span className="text-[#39FF14] font-bold">Run Simulation</span> to
              Monte-Carlo it against real DraftKings payout curves. Expected results plot along the curve above.
            </div>
          </div>
        </div>
      )}

      {/* Per-lineup table */}
      {result?.lineups && result.lineups.length > 0 && (
        <div className="max-h-[480px] overflow-y-auto overflow-x-auto">
          <table data-testid="contest-sim-lineups" className="w-full min-w-[600px] text-xs mono">
            <thead className="bg-black sticky top-0 z-10">
              <tr className="text-left text-zinc-400 uppercase tracking-widest text-[10px]">
                <th className="px-3 py-2">Lineup</th>
                <th className="px-3 py-2 text-right">Proj</th>
                <th className="px-3 py-2 text-right">Exp Payout</th>
                <th className="px-3 py-2 text-right">Exp ROI %</th>
                <th className="px-3 py-2 text-right">Cash %</th>
                <th className="px-3 py-2 text-right">Top 1%</th>
                <th className="px-3 py-2 text-right">Best Seen</th>
              </tr>
            </thead>
            <tbody>
              {[...result.lineups].sort((a, b) => b.expected_roi_pct - a.expected_roi_pct).map((L) => (
                <tr key={L.lineup_id} className="border-t border-zinc-900 hover:bg-zinc-950">
                  <td className="px-3 py-2">#{L.lineup_id}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">{L.projection}</td>
                  <td className="px-3 py-2 text-right">{fmtDollars(L.expected_payout)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${L.expected_roi_pct > 0 ? "text-[#39FF14]" : L.expected_roi_pct > -30 ? "text-[#FFCC00]" : "text-[#FF3B30]"}`}>
                    {L.expected_roi_pct > 0 ? "+" : ""}{L.expected_roi_pct.toFixed(1)}%
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-300">{L.cash_rate_pct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{L.top_1pct_rate_pct.toFixed(2)}%</td>
                  <td className="px-3 py-2 text-right text-[#FFCC00]">{fmtDollars(L.max_observed_payout)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
